/*
 * <free-gift-progress> — drives the two-tier cart-drawer progress bar's gift tier.
 *
 * Rendered by snippets/free-shipping-progress-bar-v2.liquid on every cart-drawer
 * render. Because the definition is registered once here, each freshly-injected
 * element upgrades and runs connectedCallback (custom elements upgrade on
 * innerHTML insertion — unlike <script src>, see CLAUDE.md lesson).
 *
 * Responsibilities (all keyed off the server-computed data-* attributes):
 *   - Manual add: click on .free-gift-add-btn adds the gift variant.
 *   - Auto-add: when data-auto-add and data-gift-qualified.
 *   - Auto-remove: when the gift is in the cart but data-gift-qualified has
 *     gone false (the Cart Transform function stops zeroing it → the gift would
 *     otherwise ring up at full price).
 *   - Qty guard: pin the gift line to quantity 1.
 *
 * data-gift-qualified is computed server-side by free-gift-cart-qualified and
 * covers BOTH unlock modes — spend threshold, or a bundle in the cart. This file
 * deliberately does no threshold arithmetic: adding a second mode meant either
 * teaching it a second rule or giving it one flag, and one flag keeps the rule
 * in a single auditable place next to the function that actually prices the gift.
 *
 * Each add/remove/qty change flips the condition it acted on, so the post-render
 * re-evaluation is a no-op — no feedback loop. A static `busy` flag serializes
 * overlapping cart mutations within the async refresh window.
 */
if (!customElements.get('free-gift-progress')) {
  class FreeGiftProgress extends HTMLElement {
    connectedCallback() {
      const addBtn = this.querySelector('.free-gift-add-btn');
      if (addBtn) {
        addBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.addGift(addBtn);
        });
      }

      // The gift's remove button lives in the cart line list, OUTSIDE this
      // element, and both are replaced on every re-render — so this delegate
      // goes on document, once, guarded by a static flag (connectedCallback
      // runs again for every freshly-injected element).
      if (!FreeGiftProgress.declineBound) {
        FreeGiftProgress.declineBound = true;
        document.addEventListener('click', FreeGiftProgress.onRemoveClick, true);
      }

      this.evaluate();
    }

    // Capture phase, so this runs BEFORE Dawn's CartRemoveButton click handler.
    // It does NOT replace the removal — it only slips the decline flag in front
    // of it, then hands the delete back to Dawn so the spinner, the section
    // re-render and the cart events are all the native ones. Doing the removal
    // by hand here is what made this button feel broken: no spinner, and two
    // round trips before anything visibly happened.
    static onRemoveClick(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      const removeBtn = target.closest('cart-remove-button');
      if (!removeBtn || !removeBtn.closest('.cart-item--free-gift')) return;

      const el = document.querySelector('free-gift-progress');
      const cartItems = removeBtn.closest('cart-drawer-items') || removeBtn.closest('cart-items');
      const line = removeBtn.dataset.index;
      if (!el || !cartItems || !line) return;

      event.preventDefault();
      event.stopPropagation();

      // Spinner first, synchronously, so the button reacts on the same frame as
      // every other delete button. updateQuantity calls this again itself; it is
      // idempotent, and calling it here is what covers the flag round trip.
      cartItems.enableLoading(line);

      // Flag BEFORE the removal: the re-render that follows must already see the
      // decline, or evaluate() auto-adds the gift straight back.
      el.recordDecline().then(function () {
        cartItems.updateQuantity(line, 0, event);
      });
    }

    // Attribute-only write. A failed flag write must not block the delete the
    // customer asked for — the worst case is the gift re-adding, which is no
    // worse than the behaviour before any of this existed.
    recordDecline() {
      const { update } = this.routes();
      return this.postJSON(update, { attributes: { _free_gift_declined: 'true' } }).catch(() => {});
    }

    // Manual add is an opt-IN, so it retires an earlier decline and auto-add
    // resumes. Best-effort: never fail the add because this call failed.
    clearDecline() {
      const { update } = this.routes();
      return this.postJSON(update, { attributes: { _free_gift_declined: '' } }).catch(() => {});
    }

    // Accept: application/json is REQUIRED — routes.cart_update_url has no .js
    // suffix, and without the header Shopify returns the HTML cart page
    // (see the CLAUDE.md lesson on /cart/update).
    postJSON(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json());
    }

    intAttr(name) {
      return parseInt(this.dataset[name], 10) || 0;
    }

    evaluate() {
      if (FreeGiftProgress.busy) return;

      // "Is the gift configured at all" is the gift VARIANT, not the threshold.
      // The threshold is 0 in bundle mode, so the old `threshold <= 0` bail-out
      // would have disabled every branch below on exactly the mode this element
      // now has to serve.
      if (!parseInt(this.dataset.giftVariant, 10)) return;

      // Server-computed by free-gift-cart-qualified, so BOTH unlock modes
      // (spend threshold / bundle in cart) come down one code path and this
      // element does no threshold maths of its own.
      const qualified = this.dataset.giftQualified === 'true';
      const inCart = this.dataset.giftInCart === 'true';
      const qty = this.intAttr('giftQty');
      const autoAdd = this.dataset.autoAdd === 'true';
      const declined = this.dataset.giftDeclined === 'true';

      // Cart no longer qualifies — pull the gift so it can't ring up full price.
      // (The Cart Transform function refuses to zero it too, so leaving it in
      // would mean charging retail for something labelled "Free".)
      if (inCart && !qualified) {
        this.removeGift();
        return;
      }
      // Gift is a single freebie; never allow more than one.
      if (inCart && qty > 1) {
        this.setGiftQtyOne();
        return;
      }
      // A decline belongs to the cart that qualified when it was made. Cart
      // attributes OUTLIVE the line items — emptying the cart does not clear
      // them — so without this the flag would suppress auto-add forever, and the
      // merchant's auto-add setting would silently look broken. Ceasing to
      // qualify ends the offer, so qualifying again later is a fresh one. No
      // refresh: nothing renders differently down here, and the next render
      // picks the cleared attribute up.
      if (declined && !qualified) {
        this.clearDecline();
        return;
      }
      // Auto-add path (manual add is handled by the button click). Gated on
      // availability so a sold-out gift isn't re-attempted on every drawer
      // render, and on the decline flag so a gift the customer deliberately
      // removed isn't put straight back — they re-add it from the card's button.
      if (autoAdd && !declined && !inCart && qualified && this.dataset.giftAvailable === 'true') {
        this.addGift();
      }
    }

    routes() {
      const win = window;
      return {
        root: (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/',
        add: (win.routes && win.routes.cart_add_url) || '/cart/add.js',
        change: (win.routes && win.routes.cart_change_url) || '/cart/change.js',
        update: (win.routes && win.routes.cart_update_url) || '/cart/update.js',
      };
    }

    addGift(btn) {
      // Test-mode gate: while free-gift test mode is on (data-ab-test="true"),
      // only a visitor with html.fg-gift-variant (the URL trigger in
      // layout/theme.liquid, or a testing tool's variant) may add the gift.
      // Outside test mode (permanent mode) data-ab-test is empty, so this check
      // is a no-op.
      if (this.dataset.abTest === 'true' && !document.documentElement.classList.contains('fg-gift-variant')) return;
      const variantId = parseInt(this.dataset.giftVariant, 10);
      if (!variantId || FreeGiftProgress.busy) return;
      FreeGiftProgress.busy = true;

      if (btn) {
        btn.classList.add('fgp__gift-add--loading');
        btn.setAttribute('aria-disabled', 'true');
      }

      const { root, add } = this.routes();
      const item = {
        id: variantId,
        quantity: 1,
        properties: { _free_gift: 'true' },
      };

      // XHR, not fetch — Appstle monkey-patches window.fetch on /cart/add.js
      // (see cart-drawer.js xhrPost + CLAUDE.md lesson). The gift is one-time
      // (no selling_plan) so it stays a freebie, not a subscription.
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', add);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.onload = () => {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (err) {
            reject(err);
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(JSON.stringify({ items: [item] }));
      })
        .then((res) => {
          if (res && res.status) throw new Error(res.description || 'Add failed');
          // Only pay for the extra request when there is actually a decline to
          // retire — the common auto-add path skips it.
          if (this.dataset.giftDeclined === 'true') {
            return this.clearDecline().then(() => this.refresh(root));
          }
          return this.refresh(root);
        })
        .catch((err) => {
          console.error('Free gift add error:', err);
          if (btn) {
            btn.classList.remove('fgp__gift-add--loading');
            btn.removeAttribute('aria-disabled');
          }
          FreeGiftProgress.busy = false;
        });
    }

    changeLine(quantity) {
      const key = this.dataset.giftLineKey;
      if (!key || FreeGiftProgress.busy) return;
      FreeGiftProgress.busy = true;

      const { root, change } = this.routes();
      // /cart/change.js needs Accept: application/json or it returns the HTML
      // cart page (CLAUDE.md lesson). No selling_plan involved here.
      fetch(change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then((r) => r.json())
        .then((res) => {
          // change.js returns the cart on success; an error payload carries `status`.
          // On failure, clear busy WITHOUT refreshing — the next genuine cart event
          // re-renders with a fresh line key and re-attempts. Never loops on itself.
          if (res && res.status) {
            throw new Error(res.description || res.message || 'Cart change failed');
          }
          return this.refresh(root);
        })
        .catch((err) => {
          console.error('Free gift change error:', err);
          FreeGiftProgress.busy = false;
        });
    }

    removeGift() {
      this.changeLine(0);
    }

    setGiftQtyOne() {
      this.changeLine(1);
    }

    // Re-render the drawer from the Section Rendering API and swap its inner
    // HTML — mirrors the smart-upsell refresh in cart-drawer.liquid. The new
    // <free-gift-progress> upgrades during the swap; busy is cleared afterward
    // so the next genuine cart change (not this settled one) can act.
    refresh(root) {
      return fetch(root + '?sections=cart-drawer')
        .then((r) => r.json())
        .then((sections) => {
          const html = sections['cart-drawer'];
          if (!html) throw new Error('cart-drawer section missing from refresh');

          const temp = document.createElement('div');
          temp.innerHTML = html;

          const sourceInner = temp.querySelector('.drawer__inner');
          const targetInner = document.querySelector('.drawer__inner');
          if (targetInner && sourceInner) {
            targetInner.innerHTML = sourceInner.innerHTML;
          }

          const cartDrawer = document.querySelector('cart-drawer');
          const sourceCartDrawer = temp.querySelector('cart-drawer');
          if (cartDrawer && sourceCartDrawer) {
            cartDrawer.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
            // The smart-upsell handler re-binds itself when this flag is reset.
            cartDrawer.__smartUpsellBound = false;
          }

          // NOTE: #CartDrawer-Overlay lives OUTSIDE .drawer__inner, so this swap
          // never replaces it and its original cart-drawer.js close listener stays
          // bound. Do NOT re-add one here — that leaks a handler on every mutation.

          const bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden]');
          if (bubbles.length) {
            fetch(root + 'cart.js')
              .then((r) => r.json())
              .then((cart) => {
                bubbles.forEach((b) => { b.textContent = cart.item_count; });
              })
              .catch(() => {});
          }
        })
        .finally(() => {
          FreeGiftProgress.busy = false;
        });
    }
  }

  FreeGiftProgress.busy = false;
  FreeGiftProgress.declineBound = false;
  customElements.define('free-gift-progress', FreeGiftProgress);
}
